import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { getInvalidationKeys, replayMutation } from "@/lib/mutationRegistry";
import { refreshPreparedDatasetsForKeys } from "@/lib/offline";
import {
	getSnapshot as reachabilitySnapshot,
	subscribe as reachabilitySubscribe,
} from "@/lib/offline/reachability";
import { loadReplayHandler } from "@/lib/offline/replayHandlers";
import {
	listQueue,
	type QueueItem,
	removeQueueItem,
	subscribeToQueue,
	updateQueueItem,
} from "@/lib/offlineQueue";

export type QueueStatus = "idle" | "replaying";

export interface UseOfflineQueueResult {
	items: QueueItem[];
	depth: number;
	status: QueueStatus;
	replayingIds: ReadonlySet<string>;
	replayItem: (id: string) => Promise<void>;
	replayAll: () => Promise<void>;
	discardItem: (id: string) => Promise<void>;
	refresh: () => Promise<void>;
}

export function useOfflineQueue(): UseOfflineQueueResult {
	const queryClient = useQueryClient();
	const [items, setItems] = useState<QueueItem[]>([]);
	const [status, setStatus] = useState<QueueStatus>("idle");
	const replayingRef = useRef(false);
	const inFlightRef = useRef(new Set<string>());
	const [replayingIds, setReplayingIds] = useState<ReadonlySet<string>>(
		new Set(),
	);

	const refresh = useCallback(async () => {
		const q = await listQueue();
		setItems(q);
	}, []);

	// Load queue on mount, refresh whenever queue changes, and on reconnect.
	useEffect(() => {
		refresh();
		const unsubQueue = subscribeToQueue(refresh);
		const unsubReachability = reachabilitySubscribe(() => {
			if (reachabilitySnapshot()) {
				// Came back online — refresh queue list so the count is current.
				setTimeout(refresh, 1000);
			}
		});
		return () => {
			unsubQueue();
			unsubReachability();
		};
	}, [refresh]);

	// Returns the keys the replayed write declared, so the caller decides when
	// to re-prepare the offline datasets they name.
	const replayQueued = useCallback(
		async (id: string): Promise<QueryKey[]> => {
			// Claim the id before the first await: two taps in one tick would
			// otherwise both read the same queue item and POST it twice.
			if (inFlightRef.current.has(id)) return [];
			inFlightRef.current.add(id);
			setReplayingIds(new Set(inFlightRef.current));
			try {
				const current = await listQueue();
				const item = current.find((i) => i.id === id);
				if (!item) return [];

				await loadReplayHandler(item.offlineQueueKey);
				const result = await replayMutation(
					item.offlineQueueKey,
					item.payload,
					item.payloadVersion,
				);

				let keys: QueryKey[] = [];
				if (result.type === "ok") {
					// Run the registered success effect the per-component `onSuccess`
					// can't run on replay, so mounted lists refresh (D4). Keys derive
					// from the persisted payload alone — no dead React closure state.
					keys = getInvalidationKeys(item.offlineQueueKey, item.payload);
					await Promise.all(
						keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
					);
					await removeQueueItem(id);
				} else {
					await updateQueueItem(id, {
						// A pure connectivity failure isn't evidence the item is stuck —
						// don't walk it toward the "stuck" badge while simply offline (D3).
						...(result.type === "network-error"
							? {}
							: { retries: item.retries + 1 }),
						lastError: result,
					});
				}
				await refresh();
				return keys;
			} finally {
				inFlightRef.current.delete(id);
				setReplayingIds(new Set(inFlightRef.current));
			}
		},
		[refresh, queryClient],
	);

	// Offline datasets have no observer, so the invalidation in `replayQueued`
	// only flags them stale. Don't hold the queue up on their downloads.
	const replayItem = useCallback(
		async (id: string) => {
			void refreshPreparedDatasetsForKeys(queryClient, await replayQueued(id));
		},
		[replayQueued, queryClient],
	);

	const replayAll = useCallback(async () => {
		if (replayingRef.current) return;
		replayingRef.current = true;
		setStatus("replaying");
		try {
			// FIFO — process oldest first, one at a time (single-shot per spec §4 Decision 13).
			const current = await listQueue();
			const sorted = [...current].sort((a, b) => a.enqueuedAt - b.enqueuedAt);
			const replayedKeys: QueryKey[] = [];
			for (const item of sorted) {
				replayedKeys.push(...(await replayQueued(item.id)));
			}
			void refreshPreparedDatasetsForKeys(queryClient, replayedKeys);
		} finally {
			replayingRef.current = false;
			setStatus("idle");
		}
	}, [replayQueued, queryClient]);

	const discardItem = useCallback(
		async (id: string) => {
			await removeQueueItem(id);
			await refresh();
		},
		[refresh],
	);

	return {
		items,
		depth: items.length,
		status,
		replayingIds,
		replayItem,
		replayAll,
		discardItem,
		refresh,
	};
}
